import { PLAN_TIER_INFO } from "@gf/shared";
import { discountedCents, type SignupCard, type SignupDiscount } from "@gf/shared/signup";
import { Button, Callout, cx, Skeleton, useTheme } from "@gf/ui";
import { Lock } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { loadStripe, STRIPE_FONTS, stripeAppearance, type StripeClient, type StripeElements } from "../../../lib/stripe";
import { CARD_RETURN_PATH, confirmOutcome, trialButtonLabel, trialDisclosure } from "./flow";
import { LegalLine } from "./SignupBits";
import { trackSignupStep } from "../../../lib/analytics";

const LOAD_FAILED =
  "The secure card form didn't load. Check your connection, allow js.stripe.com if you use a content blocker, then try again.";

/**
 * Stripe's Payment Element for the card-check PaymentIntent, the trial disclosure, and the button.
 * On a decline Stripe's message shows and the same form takes another card. When the hold is in
 * place (requires_capture) it hands over to `onConfirmed`, which activates the trial.
 */
export function PaymentForm({
  card,
  name,
  email,
  discount,
  disabled,
  error: outsideError,
  onConfirmed,
}: {
  card: SignupCard;
  name: string;
  email: string;
  /** The signup's discount code: the price after the trial is shown discounted. */
  discount?: SignupDiscount | null;
  /** e.g. while the plan is being changed. */
  disabled?: boolean;
  /** An error from outside (the API said the card didn't check out). Cleared on the next attempt. */
  error?: string | null;
  onConfirmed: () => void;
}) {
  const { theme } = useTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const mountRef = useRef<HTMLDivElement>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const [stripe, setStripe] = useState<StripeClient | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(outsideError ?? null);
  const autoActivate = !outsideError;
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  useEffect(() => setError(outsideError ?? null), [outsideError]);

  // Mount the Payment Element for this PaymentIntent (again after "Try again").
  useEffect(() => {
    let cancelled = false;
    let element: ReturnType<StripeElements["create"]> | null = null;
    setReady(false);
    setLoadError(null);
    loadStripe(card.publishableKey)
      .then(async (client) => {
        // The hold may already be in place (confirmed, then the page was left before activation, or 3-D
        // Secure finished in another tab): skip the form and activate. Not after the API just turned this
        // card down, so a rejected hold can't loop.
        const current = autoActivate ? await client.retrievePaymentIntent(card.clientSecret).catch(() => null) : null;
        if (cancelled) return;
        if (current?.paymentIntent?.status === "requires_capture") {
          setBusy(true);
          onConfirmedRef.current();
          return;
        }
        if (!mountRef.current) return;
        const elements = client.elements({ clientSecret: card.clientSecret, appearance: stripeAppearance(themeRef.current), fonts: STRIPE_FONTS });
        element = elements.create("payment", {
          layout: "tabs",
          defaultValues: { billingDetails: { name, email } },
          business: { name: "GramFather" },
          // Cards only: Link's bank option can't take the card-check hold (and its promo confuses the trial).
          wallets: { link: "never" },
        });
        element.on("ready", () => {
          if (!cancelled) setReady(true);
        });
        element.on("loaderror", (e) => {
          if (!cancelled) setLoadError(e.error?.message || LOAD_FAILED);
        });
        element.mount(mountRef.current);
        elementsRef.current = elements;
        setStripe(client);
      })
      .catch(() => {
        if (!cancelled) setLoadError(LOAD_FAILED);
      });
    return () => {
      cancelled = true;
      element?.destroy();
      elementsRef.current = null;
    };
    // name/email only prefill the form; a change must not remount it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.clientSecret, card.publishableKey, reloads]);

  // Follow the light/dark toggle.
  useEffect(() => {
    elementsRef.current?.update({ appearance: stripeAppearance(theme) });
  }, [theme]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const elements = elementsRef.current;
    if (!stripe || !elements || busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: `${window.location.origin}${CARD_RETURN_PATH}` },
      });
      if (result.error) {
        // Field problems show inside the form; everything else (declines included) shows here.
        if (result.error.type !== "validation_error") setError(result.error.message || "That card didn't go through. Try another card.");
        setBusy(false);
        return;
      }
      switch (confirmOutcome(result.paymentIntent?.status)) {
        case "activate":
          trackSignupStep("card_confirmed");
          onConfirmed();
          return; // stay busy: the page moves on to activation
        case "processing":
          setError("Your bank is still confirming the card. Wait a moment, then try again.");
          break;
        case "incomplete":
          setError("Your bank needs you to finish confirming the card. Try again and follow its prompt.");
          break;
        case "declined":
          setError("That card didn't go through. Try another card.");
          break;
      }
    } catch {
      setError("We couldn't reach Stripe to check your card. Check your connection and try again.");
    }
    setBusy(false);
  }

  const plan = PLAN_TIER_INFO[card.plan];
  const terms = trialDisclosure({
    trialDays: card.trialDays,
    holdCents: card.amountCents,
    priceCents: discountedCents(plan.priceMonthly * 100, discount),
    trialEndsAt: card.trialEndsAt,
    discountLabel: discount?.label ?? null,
  });
  const canSubmit = !!stripe && ready && !loadError && !disabled;

  return (
    <form className="gfp-auth__form" onSubmit={onSubmit} noValidate>
      <div className="signup-card">
        {!ready && !loadError ? (
          <div className="signup-card__loading" aria-hidden="true">
            <Skeleton height={14} width="30%" />
            <Skeleton height={44} radius={6} />
            <div className="signup-card__row">
              <Skeleton height={44} radius={6} />
              <Skeleton height={44} radius={6} />
            </div>
          </div>
        ) : null}
        <div ref={mountRef} className={cx("signup-card__element", !ready && "signup-card__element--loading")} hidden={!!loadError} />
        {loadError ? (
          <Callout
            tone="danger"
            title="Card form unavailable"
            action={
              <Button size="sm" onClick={() => setReloads((n) => n + 1)}>
                Try again
              </Button>
            }
          >
            {loadError}
          </Callout>
        ) : null}
      </div>

      {error ? (
        <Callout tone="danger" title="Card not accepted">
          {error}
        </Callout>
      ) : null}

      <div className="signup-terms" role="note" aria-label="Trial terms">
        <p className="signup-terms__lead">{terms.lead}</p>
        <p>{terms.hold}</p>
        <p>{terms.cancel}</p>
        <p>{terms.refunds}</p>
      </div>

      <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!canSubmit}>
        {trialButtonLabel(card.trialDays)}
      </Button>
      <p className="signup-fine signup-fine--center">
        <Lock size={13} aria-hidden="true" /> Card handled by Stripe. GramFather never sees your card number.
        <br />
        <LegalLine prefix="By starting your trial, you agree to the" />
      </p>
    </form>
  );
}
